import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useReanalyzeFollowup, useFollowupDecisionOverride, type MessageDetail } from '../../hooks/useEmail.js';
import { Button } from '../ui/Button.js';
import { IconButton } from '../ui/IconButton.js';

type Decision = 'followup_yes' | 'followup_no' | 'followup_uncertain';
type Priority = 'alta' | 'media' | 'baja';

// Soft skips: analyzed but excluded by category/domain/direction
const SOFT_SKIP_REASONS = new Set(['category_skip', 'domain_rule', 'outbound', 'list-unsubscribe']);
// Hard-rule excludes: heuristic blocked before AI analysis
const HARD_RULE_REASONS = new Set(['list-unsubscribe', 'sender-pattern']);

function relativeTime(isoStr: string): string {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'hace un momento';
  if (mins < 60) return `hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  return `hace ${Math.floor(hrs / 24)}d`;
}

const DECISION_STYLE: Record<Decision, { bg: string; fg: string; border: string }> = {
  followup_yes:       { bg: 'color-mix(in srgb, var(--evo-amber) 10%, transparent)',    fg: 'var(--evo-amber)',    border: 'color-mix(in srgb, var(--evo-amber) 25%, transparent)' },
  followup_uncertain: { bg: 'color-mix(in srgb, var(--evo-blue-500) 10%, transparent)', fg: 'var(--evo-blue-500)', border: 'color-mix(in srgb, var(--evo-blue-500) 25%, transparent)' },
  followup_no:        { bg: 'color-mix(in srgb, var(--evo-fg-3) 10%, transparent)',     fg: 'var(--evo-fg-3)',     border: 'color-mix(in srgb, var(--evo-fg-3) 25%, transparent)' },
};

const PRIORITY_STYLE: Record<Priority, { bg: string; fg: string; border: string }> = {
  alta:  { bg: 'color-mix(in srgb, var(--evo-amber) 10%, transparent)',    fg: 'var(--evo-amber)',    border: 'color-mix(in srgb, var(--evo-amber) 25%, transparent)' },
  media: { bg: 'color-mix(in srgb, var(--evo-blue-300) 10%, transparent)', fg: 'var(--evo-blue-300)', border: 'color-mix(in srgb, var(--evo-blue-300) 25%, transparent)' },
  baja:  { bg: 'color-mix(in srgb, var(--evo-fg-3) 10%, transparent)',     fg: 'var(--evo-fg-3)',     border: 'color-mix(in srgb, var(--evo-fg-3) 25%, transparent)' },
};

function decisionI18nKey(d: Decision): string {
  return d === 'followup_yes' ? 'decision_yes' : d === 'followup_no' ? 'decision_no' : 'decision_uncertain';
}

function deriveSource(isManual: boolean, heuristicReason: string | null, hasDecision: boolean): string | null {
  if (isManual) return 'manual';
  if (heuristicReason === 'subject-match') return 'subject_match';
  if (heuristicReason === 'sender-pattern') return 'sender_pattern';
  if (heuristicReason === 'list-unsubscribe') return 'list_unsubscribe';
  if (hasDecision) return 'auto';
  return null;
}

export function FollowupAnalysisPanel({
  messageId,
  detail,
}: {
  messageId: number;
  detail: Pick<
    MessageDetail,
    | 'followupDecision'
    | 'followupPriority'
    | 'followupReasoning'
    | 'followupConfidence'
    | 'followupAnalyzedAt'
    | 'followupManuallySet'
    | 'followupReason'
  >;
}) {
  const { t } = useTranslation();
  const reanalyze = useReanalyzeFollowup();
  const override = useFollowupDecisionOverride();

  const decision = (detail.followupDecision ?? null) as Decision | null;
  const priority = (detail.followupPriority ?? null) as Priority | null;
  const reasoning = detail.followupReasoning ?? null;
  const heuristicReason = detail.followupReason ?? null;
  const isManual = detail.followupManuallySet === true;

  // Hard-rule excluded: heuristic blocked before AI got a chance
  const isHardRuleExcluded = heuristicReason ? HARD_RULE_REASONS.has(heuristicReason) : false;
  // Soft skip: AI ran but decided no analysis needed (category/domain/direction)
  const isSkipped = !isHardRuleExcluded && decision === 'followup_no' && SOFT_SKIP_REASONS.has(reasoning ?? '');
  // Failed: analyzer wrote an error decision
  const isFailed = (detail.followupDecision as string | null) === 'followup_failed';
  // Pending: no decision yet and analyzer hasn't run
  const isPending = !isHardRuleExcluded && !isSkipped && !isFailed && !decision && !detail.followupAnalyzedAt;
  // Full analysis available
  const hasAnalysis = !isPending && !isSkipped && !isHardRuleExcluded && !isFailed && decision !== null;

  const source = deriveSource(isManual, heuristicReason, hasAnalysis);
  const borderAccent = decision && decision in DECISION_STYLE
    ? DECISION_STYLE[decision].fg
    : 'var(--evo-stroke)';

  const shouldStartOpen = decision === 'followup_yes' || decision === 'followup_uncertain';
  const [collapsed, setCollapsed] = useState(!shouldStartOpen);
  const [toast, setToast] = useState('');

  useEffect(() => {
    setCollapsed(decision !== 'followup_yes' && decision !== 'followup_uncertain');
  }, [decision]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }

  function handleReanalyze() {
    reanalyze.mutate(messageId, {
      onSuccess: () => showToast(t('email.followup.toast_reanalyzed')),
    });
  }

  function handleOverride(dec: Decision, pri?: Priority) {
    override.mutate({ id: messageId, decision: dec, ...(pri ? { priority: pri } : {}) }, {
      onSuccess: () => {
        if (dec !== 'followup_no') setCollapsed(false);
        showToast(t('email.followup.toast_overridden'));
      },
    });
  }

  return (
    <div
      className="px-4 py-2 shrink-0"
      style={{ borderBottom: '1px solid var(--evo-stroke)', background: 'var(--evo-bg-2)' }}
    >
      {toast && (
        <div
          className="mb-1.5 text-xs px-2 py-1 rounded"
          style={{ background: 'color-mix(in srgb, var(--evo-green-300) 12%, transparent)', color: 'var(--evo-green-300)', border: '1px solid color-mix(in srgb, var(--evo-green-300) 25%, transparent)' }}
        >
          {toast}
        </div>
      )}

      {/* ── Header row ───────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span
          className="text-[11px] font-medium"
          style={{ color: 'var(--evo-text-muted)', textTransform: 'uppercase', letterSpacing: '0.88px' }}
        >
          {t('email.followup.title')}
        </span>

        {hasAnalysis && decision && (
          <>
            <span
              className="text-[10px] px-1.5 py-0.5 rounded font-medium"
              style={{
                background: DECISION_STYLE[decision].bg,
                color: DECISION_STYLE[decision].fg,
                border: `1px solid ${DECISION_STYLE[decision].border}`,
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
              }}
            >
              {t(`email.followup.${decisionI18nKey(decision)}`)}
            </span>

            {decision === 'followup_yes' && priority && priority in PRIORITY_STYLE && (
              <span
                className="text-xs px-1.5 py-0.5 rounded"
                style={{
                  background: PRIORITY_STYLE[priority].bg,
                  color: PRIORITY_STYLE[priority].fg,
                  border: `1px solid ${PRIORITY_STYLE[priority].border}`,
                }}
              >
                {t(`email.followup.priority_${priority}`)}
              </span>
            )}

            {source && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded"
                title={t(`email.followup.source_tooltip_${source}`, { defaultValue: source })}
                style={{
                  background: 'transparent',
                  color: 'var(--evo-fg-3)',
                  border: '1px solid var(--evo-stroke)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  cursor: 'help',
                }}
              >
                {t(`email.followup.source_${source}`)}
              </span>
            )}
          </>
        )}

        <div className="ml-auto flex items-center gap-1">
          {!isPending && !isHardRuleExcluded && (
            <Button
              variant="outline"
              tone="muted"
              size="xs"
              loading={reanalyze.isPending}
              disabled={override.isPending}
              onClick={handleReanalyze}
            >
              {reanalyze.isPending ? t('email.followup.reanalyzing') : t('email.followup.reanalyze')}
            </Button>
          )}

          {hasAnalysis && (
            <IconButton
              variant="outline"
              tone="muted"
              size="sm"
              ariaLabel={collapsed ? 'Expandir' : 'Colapsar'}
              icon={<span style={{ fontSize: '12px', lineHeight: 1 }}>{collapsed ? '›' : '‹'}</span>}
              onClick={() => setCollapsed(c => !c)}
            />
          )}
        </div>
      </div>

      {/* ── State 1: Pending ─────────────────────────────────────────── */}
      {isPending && (
        <div className="py-1">
          <p className="text-xs mb-2" style={{ color: 'var(--evo-fg-3)', fontStyle: 'italic' }}>
            {t('email.followup.pending')}
          </p>
          <Button variant="solid" tone="cyan" size="sm" loading={reanalyze.isPending} onClick={handleReanalyze}>
            {reanalyze.isPending ? t('email.followup.reanalyzing') : t('email.followup.analyze_now')}
          </Button>
        </div>
      )}

      {/* ── State 2: Soft skip (category, domain rule, outbound) ─────── */}
      {isSkipped && (
        <div className="py-1">
          <p className="text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>
            {t('email.followup.skipped')}
          </p>
          {reasoning && (
            <p className="text-xs mb-2" style={{ color: 'var(--evo-fg-3)', fontStyle: 'italic' }}>
              {t(`email.followup.skipped_${reasoning}`, { defaultValue: '' }) || reasoning}
            </p>
          )}
          <Button variant="outline" tone="muted" size="xs" loading={reanalyze.isPending} onClick={handleReanalyze}>
            {reanalyze.isPending ? t('email.followup.reanalyzing') : t('email.followup.force_analyze')}
          </Button>
        </div>
      )}

      {/* ── State 3: Full analysis ────────────────────────────────────── */}
      {hasAnalysis && decision && !collapsed && (
        <div
          className="rounded px-3 py-2 mb-1.5"
          style={{
            borderLeft: `3px solid ${borderAccent}`,
            background: DECISION_STYLE[decision].bg,
          }}
        >
          {isManual ? (
            <p className="text-xs" style={{ color: 'var(--evo-fg-3)', fontStyle: 'italic' }}>
              {t('email.followup.manually_set')}
            </p>
          ) : (
            reasoning && (
              <>
                <p className="text-xs font-medium mb-0.5" style={{ color: 'var(--evo-fg-3)' }}>
                  {t('email.followup.reasoning_label')}
                </p>
                <p className="text-xs mb-1.5" style={{ color: 'var(--evo-fg-3)', fontStyle: 'italic', lineHeight: 1.4 }}>
                  {reasoning}
                </p>
              </>
            )
          )}

          {!isManual && detail.followupConfidence != null && (
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                {t('email.followup.confidence_label')}:
              </span>
              <div
                className="h-1 rounded-full overflow-hidden"
                style={{ background: 'var(--evo-stroke)', width: '80px' }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.round(detail.followupConfidence * 100)}%`,
                    background: DECISION_STYLE[decision].fg,
                  }}
                />
              </div>
              <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                {Math.round(detail.followupConfidence * 100)}%
              </span>
            </div>
          )}

          {detail.followupAnalyzedAt && (
            <p className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
              {t('email.followup.analyzed_at')}: {relativeTime(detail.followupAnalyzedAt)}
            </p>
          )}
        </div>
      )}

      {/* ── State 4: Failed ──────────────────────────────────────────── */}
      {isFailed && (
        <div className="py-1">
          <p className="text-xs mb-2" style={{ color: 'var(--evo-warn)' }}>
            {t('email.followup.failed')}
          </p>
          <Button variant="solid" tone="cyan" size="sm" loading={reanalyze.isPending} onClick={handleReanalyze}>
            {reanalyze.isPending ? t('email.followup.reanalyzing') : t('email.followup.retry')}
          </Button>
        </div>
      )}

      {/* ── State 5: Hard-rule excluded (sender-pattern, list-unsubscribe) */}
      {isHardRuleExcluded && (
        <div className="py-1">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="text-[10px] px-1.5 py-0.5 rounded font-medium"
              style={{
                background: 'color-mix(in srgb, var(--evo-fg-3) 10%, transparent)',
                color: 'var(--evo-fg-3)',
                border: '1px solid color-mix(in srgb, var(--evo-fg-3) 25%, transparent)',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
              }}
            >
              {t('email.followup.excluded_by_rule')}
            </span>
          </div>
          <p className="text-xs mb-2" style={{ color: 'var(--evo-fg-3)', fontStyle: 'italic' }}>
            {t('email.followup.excluded_reason', { reason: heuristicReason ?? '' })}
          </p>
          <Button variant="outline" tone="cyan" size="xs" loading={reanalyze.isPending} onClick={handleReanalyze}>
            {reanalyze.isPending ? t('email.followup.reanalyzing') : t('email.followup.override_and_analyze')}
          </Button>
        </div>
      )}

      {/* ── Override buttons ─────────────────────────────────────────── */}
      {(hasAnalysis || isSkipped) && (
        <div className="flex gap-1 flex-wrap">
          {decision !== 'followup_yes' && (
            <Button variant="outline" tone="green" size="xs" loading={override.isPending} disabled={reanalyze.isPending} onClick={() => handleOverride('followup_yes', 'media')}>
              {t('email.followup.override_yes')}
            </Button>
          )}
          {decision !== 'followup_no' && (
            <Button variant="outline" tone="muted" size="xs" loading={override.isPending} disabled={reanalyze.isPending} onClick={() => handleOverride('followup_no')}>
              {t('email.followup.override_no')}
            </Button>
          )}
          {decision !== 'followup_uncertain' && (
            <Button variant="outline" tone="amber" size="xs" loading={override.isPending} disabled={reanalyze.isPending} onClick={() => handleOverride('followup_uncertain')}>
              {t('email.followup.override_uncertain')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
